import type { Language } from '@eformulieren/engine'
import type { Text } from '@eformulieren/model'

/**
 * Elk zichtbaar tekstelement in een definitie is een paar {nl, en} (R-143). Deze
 * functie kiest de kant die de invuller heeft gekozen.
 *
 * Taal is een weergavekeuze: de vragen en de antwoordstructuur blijven dezelfde
 * en er gaat niets verloren bij het wisselen (R-144). Daarom staat de taal nergens
 * in het document en nergens in een antwoord; hij komt alleen hier langs.
 */
export function inLanguage(text: Text, language: Language): string {
  return text[language]
}

/**
 * Een tekst van de schil zelf in beide talen: een knop, een melding, een kop (R-143, R-144).
 *
 * Niet `Text` uit het model, en dat is opzet: `Text` is wat de inrichter in een definitie zet en
 * wordt bij het publiceren getoetst (niet leeg, niet alleen witruimte). Een tekst van de schil staat
 * in de code, gaat nooit door die toets en mag leeg zijn, zoals een status zonder zin. Tot deze
 * opdracht stond dit type als losse regel in negentien bestanden.
 */
export type Bilingual = Readonly<Record<Language, string>>

/**
 * De locale bij de taal van de lezer, voor `Intl` en `toLocale…String`: Brits Engels en niet
 * Amerikaans, zodat een datum als "6 November" en niet als "November 6" staat.
 *
 * Alleen in de renderer en de pagina: de engine gebruikt bewust geen `Intl` (`language.ts` in de
 * engine), want de opmaak hangt daar af van de ICU-versie van de runtime.
 */
export function localeOf(language: Language): 'nl-NL' | 'en-GB' {
  return language === 'nl' ? 'nl-NL' : 'en-GB'
}

/**
 * Een moment (ISO 8601) als tekst in de taal van de lezer, met de opmaak van de aanroeper. Een
 * moment dat geen datum is gaat ongewijzigd terug: beter de ruwe waarde dan "Invalid Date".
 */
export function formatDateTime(moment: string, language: Language, options: Intl.DateTimeFormatOptions): string {
  const date = new Date(moment)
  if (Number.isNaN(date.getTime())) return moment
  return new Intl.DateTimeFormat(localeOf(language), options).format(date)
}
